import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users } from 'lucide-react';
import { ReportHeader } from '../components/ReportHeader';
import { fmt, fmtDT, Spinner, ErrorBanner, PaginationBar, usePii, UNAVAILABLE } from '../components/ReportTableKit';
import LearnerHistoryDrawer from '../components/LearnerHistoryDrawer';

const linkStyle = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#4338ca', fontWeight: 600, textAlign: 'left', fontSize: 'inherit', fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 };

const API = process.env.REACT_APP_BACKEND_URL;

export default function InsightsCubeLearners() {
  const [period, setPeriod] = useState({ startDate: '', endDate: '' });
  const [searchInput, setSearchInput] = useState('');
  const [sort, setSort] = useState('joined_at:desc');
  const [noPlatformId, setNoPlatformId] = useState(false);
  const [applied, setApplied] = useState({ search: '', sort: 'joined_at:desc', noPlatformId: false });
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, last_page: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const pii = usePii();
  const [drill, setDrill] = useState(null);
  const PER = 100;

  const handlePeriod = useCallback(({ startDate, endDate }) => setPeriod({ startDate: startDate || '', endDate: endDate || '' }), []);

  const fetchData = useCallback(async (p, f) => {
    setLoading(true); setErr(null);
    try {
      const params = { page: p, per_page: PER, sort: f.sort };
      if (f.search) params.search = f.search;
      if (f.noPlatformId) params.platform_user_id_missing = 'true';
      if (period.startDate) params.date_from = period.startDate;
      if (period.endDate) params.date_to = period.endDate;
      const r = await axios.get(`${API}/api/lup/v2/report/learners`, { params, withCredentials: true });
      setRows(r.data?.data || []);
      setMeta({ total: r.data?.total || 0, last_page: r.data?.last_page || 1 });
      setPage(r.data?.current_page || p);
    } catch (e) {
      setErr(UNAVAILABLE('Learners')); setRows([]); setMeta({ total: 0, last_page: 1 });
    } finally { setLoading(false); }
  }, [period.startDate, period.endDate]);

  useEffect(() => { fetchData(1, applied); }, [fetchData, applied]);

  const apply = () => setApplied({ search: searchInput, sort, noPlatformId });
  const reset = () => { setSearchInput(''); setSort('joined_at:desc'); setNoPlatformId(false); setApplied({ search: '', sort: 'joined_at:desc', noPlatformId: false }); };

  return (
    <div data-testid="insights-learners-page">
      <ReportHeader reportTitle="Learners for PSH" onChange={handlePeriod} />
      <div className="page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Users size={22} color="#4338ca" /> Learners for PSH</h1>
        <p>Learners feed delivered to EAS Partner Skilling Hub (PSH) — registered, verified learners across platforms, joined date filtered via the period selector.</p>
      </div>
      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group"><label className="filter-label">Search</label>
          <input type="text" className="filter-input" placeholder="User ID, email, organization…" value={searchInput} onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && apply()} data-testid="learners-search" /></div>
        <div className="filter-group"><label className="filter-label">Sort</label>
          <select className="filter-select" value={sort} onChange={e => setSort(e.target.value)} data-testid="learners-sort">
            <option value="joined_at:desc">Joined (newest)</option><option value="joined_at:asc">Joined (oldest)</option>
            <option value="student_user_id:asc">LMS ID (asc)</option><option value="student_user_id:desc">LMS ID (desc)</option></select></div>
        <div className="filter-group"><label className="filter-label" style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={noPlatformId} onChange={e => setNoPlatformId(e.target.checked)} data-testid="learners-no-platform-id" /> No platform ID only</label></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={apply} data-testid="learners-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={reset} data-testid="learners-reset-btn">Reset</button></div>
      </div>
      <div className="table-container">
        {loading ? <Spinner /> : err ? <ErrorBanner message={err} testId="learners-error" /> : rows.length === 0 ?
          <div className="empty-state"><h3>No learners found</h3><p>Try adjusting your filters.</p></div> : (<>
            <div className="table-header"><span className="table-title">PSH Learners Feed</span><span className="table-count" style={{ marginLeft: 8 }}>({fmt(meta.total)} rows)</span></div>
            <div className="table-scroll"><table data-testid="learners-table"><thead><tr>
              <th>Platform</th><th>Platform User ID</th><th>LMS ID</th>{pii && <th>Learner</th>}{pii && <th>Email</th>}<th>Organization</th><th>Country</th><th>Joined</th>
            </tr></thead><tbody>{rows.map((row, i) => <tr key={i} data-testid={`learners-row-${i}`}>
              <td style={{ fontSize: 12 }}>{row.platform || '—'}</td>
              <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{row.platform_user_id || '—'}</td>
              <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{row.student_user_id != null ? <button style={linkStyle} onClick={() => setDrill({ lmsId: row.student_user_id, name: row.learner_name })} data-testid={`learners-lmsid-link-${i}`}>{row.student_user_id}</button> : '—'}</td>
              {pii && <td style={{ fontSize: 13 }}>{row.student_user_id != null ? <button style={linkStyle} onClick={() => setDrill({ lmsId: row.student_user_id, name: row.learner_name })} data-testid={`learners-learner-link-${i}`}>{row.learner_name || '—'}</button> : (row.learner_name || '—')}</td>}
              {pii && <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#3182ce' }}>{row.email || '—'}</td>}
              <td style={{ fontSize: 12 }}>{row.organization || '—'}</td>
              <td style={{ fontSize: 12 }}>{row.country || '—'}</td>
              <td style={{ fontSize: 12 }}>{fmtDT(row.joined_at)}</td>
            </tr>)}</tbody></table></div>
            <PaginationBar page={page} totalPages={meta.last_page} total={meta.total} perPage={PER} onPage={p => fetchData(p, applied)} testId="learners-pagination" />
          </>)}
      </div>
      {drill && <LearnerHistoryDrawer lmsId={drill.lmsId} learnerName={drill.name} pii={pii} onClose={() => setDrill(null)} />}
    </div>
  );
}
